<template>
  <div class="loading-content">
    <div class="text-center">
      <Loader v-if="loading" class="mx-auto"/>
      <div class="text-dark">{{ message }}</div>
      <div class="text-danger">{{ error }}</div>
    </div>
    <div v-if="!loading && accountChoosingRequired">
      <div class="row justify-content-center mb-5">
        <div class="col-12 col-md-8 col-lg-6">
          <div>
            <p class="text-justify">
              Please choose an account that you wish to use for initiating the payment.
            </p>
            <li class="form-check list-group-item" v-for="(account, index) in accountsInfo.accounts" :key="index">
              <input class="form-check-input" :id="account.resourceId" :value="account.resourceId" name="account" type="radio" v-model="pickedAccount" />
              <label class="form-check-label" :for="account.resourceId">
                <span>{{ account.title }}</span><br /><span>{{ account.iban }}</span>
              </label>
            </li>
          </div>
        
          <div class="form-inline justify-content-center">
            <button class="btn btn-theme" v-on:click="submitAccount">Choose account</button>
          </div>
          <div class="text-center">
            <button class="btn btn-link" v-on:click="cancelAuth">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading && userConsentRequired">
      <div class="row justify-content-center mb-5">
        <div class="col-12 col-md-8 col-lg-6">
          <div v-if="service === 'AIS'">
            <p class="text-justify">
              <strong>{{ appName }}</strong> is requesting the following access <span v-if="accountsInfo.recurringIndicator">until {{ new Date(accountsInfo.validUntil).toLocaleDateString("en-GB") }}:</span><span v-else> for the next 15 minutes:</span>
                <ul>
                  <li v-if="!accountsInfo.accountsAccess || accountsInfo.accountsAccess.includes('accounts')">account details such as type, number, currency</li>
                  <li v-if="!accountsInfo.accountsAccess || accountsInfo.accountsAccess.includes('balances')">account balance information</li>
                  <li v-if="!accountsInfo.accountsAccess || accountsInfo.accountsAccess.includes('transactions')">detailed transaction history</li>
                </ul>
              After you press "Consent" button, your payment accounts data will be shared with the third-party provider.
              By pressing "Consent" button you agree with the <a :href="termsUrl" target="_blank">terms of service</a>.
              Press "Cancel" button if you are not willing to share your payment accounts data.
            </p>
            <div>
              <p v-if="!accountsInfo.areAccountsPreselected" class="text-justify">
                Please select the accounts you want to grant access to:
              </p>
              <p v-else class="text-justify">
                You give an access to following accounts:
              </p>
            </div>
            <li class="form-check" v-for="(account, index) in accountsInfo.accounts" :key="index">
              <input v-if="!accountsInfo.areAccountsPreselected" class="form-check-input" :id="account.resourceId" :value="account.resourceId" name="account" type="checkbox" v-model="checkedAccounts" />
              <label class="form-check-label" :for="account.resourceId"><span>{{ account.title }}</span><br /><span>{{ account.iban }}</span></label>
            </li>
          </div>
          <div v-else-if="service === 'COF'">
            <strong>{{ appName }}</strong> needs your permission to make fund check requests from the following account:
            <div>
              <p v-if="accountsInfo.account"><span>{{ accountsInfo.account.title }}</span><br /><span>{{ accountsInfo.account.iban }}</span></p>
              <p v-else class="text-danger">Account is not available</p>
            </div>
            <p class="text-justify">
              After you press "Consent" button, the third-party provider can make fund check requests from your account.
              When you make payments using a service of the third-party provider, it needs to do this check to confirm that you have enough funds.
              Only a 'Yes' or 'No' answer will be requested.
              By pressing "Consent" button you agree with the <a :href="termsUrl" target="_blank">terms of service</a>.
              Press "Cancel" button if you are not willing to share your accounts data.
            </p>
          </div>
          <div v-else-if="service === 'PIS'">
            <p><strong>Payment details</strong></p>
            <p>Payment account: <strong>{{ paymentInfo.debtorAccount.identification }} ({{ paymentInfo.debtorAccountCurrency }}, {{ paymentInfo.debtorAccountName }})</strong></p>
            <p>Account number where to transfer funds: <strong>{{ paymentInfo.creditorAccount.identification }}</strong></p>
            <p>Payee name in the payment: <strong>{{ paymentInfo.creditorName }}</strong></p>
            <p v-if="paymentInfo.creditorAddress && paymentInfo.creditorAddress.country">Payee country: <strong>{{ countryNames[paymentInfo.creditorAddress.country] || paymentInfo.creditorAddress.country }}</strong></p>
            <p>Payment amount: <strong>{{ paymentInfo.amount }} {{ paymentInfo.currency }}</strong></p>
            <p>Payment fee: <strong>{{ paymentInfo.feeAmount }} {{ paymentInfo.feeCurrency }}</strong></p>
            <p>Message or reference number in the payment: <strong>{{ paymentInfo.remittanceInformation }}</strong></p>
            <p class="text-justify">
              Payment is initiated by <strong>{{ appName }}</strong>.
              After you press "Consent" button, this payment will be placed for execution.
              By pressing "Consent" button you agree with the <a :href="termsUrl" target="_blank">terms of service</a>.
              Press "Cancel" button if you are not willing to complete this payment.
            </p>
          </div>
          <div class="form-inline justify-content-center">
            <button class="btn btn-theme" v-on:click="submitDataSharingConsent">Consent</button>
          </div>
          <div class="text-center">
            <button class="btn btn-link" v-on:click="cancelAuth">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import constants from '../constants.js'
import Loader from '../components/elements/Loader.vue'
import BaseService from '../services/BaseService.js'

export default {
  name: 'home',
  components: {
    Loader
  },
  data () {
    return {
      countryNames: constants.countriesNames,
      error: '',
      loading: true,
      message: '',
      service: null,
      prevStatus: null,
      status: null,
      appName: '',
      appDescription: '',
      accountsInfo: null,
      paymentInfo: null,
      userConsentRequired: false,
      accountChoosingRequired: false,
      pickedAccount: '',
      checkedAccounts: [],
      updates: 0,
      termsUrl: '/terms'
    }
  },
  methods: {
    async handleWaitingConsentConfirmation (data) {
      this.appName = data.applicationName
      this.appDescription = data.applicationDescription
      this.userConsentRequired = true
      this.accountsInfo = data.accountsInfo
      this.paymentInfo = data.paymentInfo
      this.termsUrl = data.termsUrl || this.termsUrl
      this.loading = false
    },
    async handleAccountChoosing (data) {
      this.accountChoosingRequired = true
      this.accountsInfo = data.accountsInfo
      this.loading = false
    },
    async handleCancelled (data) {
      this.error = 'Consent rejected'
      if (data.redirectUri) {
        this.message = 'Returning to the third-party provider'
        this.redirect(data.redirectUri)
      } else {
        this.message = 'You need to close this window'
      }
    },
    async handleReturnedToTpp (data) {
      this.message = ''
      this.scheduleUpdate(500)
    },
    async handleTppRedirect (data) {
      this.message = 'Redirecting to the third-party provider'
      const r = await BaseService.notifyLeaving()
      if (r.data.message !== 'OK') {
        throw new Error(r.data.message)
      }
      this.redirect(data.redirectUri)
    },
    redirect (redirectUrl) {
      setTimeout(() => {
        window.location.href = redirectUrl
      }, 500)
    },
    async updateStatus () {
      try {
        this.updates += 1
        const r = await BaseService.getSession()
        this.error = ''
        this.prevStatus = this.status
        this.service = r.data.service
        this.status = r.data.status
        const handlerName = this.status
          .split('_')
          .map(e => e.substr(0, 1) + e.substr(1).toLowerCase())
          .join('')
        const handler = this[`handle${handlerName}`]
        if (!handler) {
          this.message = ''
          throw new Error(`Unable to handle authentication ${this.status}`)
        }
        handler(r.data)
      } catch (e) {
        let retry = true
        if (e.response) {
          if (e.response.status === 404) {
            this.error = 'Unable to access server'
            retry = false
          } else if (e.response.status === 422 &&
            e.response.data &&
            e.response.data.detail &&
            e.response.data.detail.find(d =>
              d.loc[0] === 'cookie' &&
              d.loc[1] === 'sessionid' &&
              d.type === 'value_error.missing')
          ) {
            try {
              await BaseService.restoreSession(window.location.href)
            } catch (e) {
              this.error = 'Authentication session has expired'
            }
          } else {
            this.error = 'Error while updating authentication status'
          }
        } else if (e.message) {
          this.error = e.message
        } else {
          this.error = 'Unknown error occurred'
        }
        this.loading = retry
        if (retry) {
          this.scheduleUpdate(500)
        }
      }
    },
    scheduleUpdate (timeout, loading = true) {
      this.loading = loading
      if (this.updates > 5) {
        timeout = timeout * Math.ceil(this.updates / 5)
      }
      setTimeout(this.updateStatus, timeout)
    },
    async submitDataSharingConsent () {
      this.loading = true
      try {
        const r = await BaseService.confirmConsent({ checkedAccounts: this.checkedAccounts })
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.userConsentRequired = false
      this.scheduleUpdate(500)
    },
    async submitAccount () {
      this.loading = true
      try {
        const r = await BaseService.confirmAccount({ pickedAccount: this.pickedAccount })
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.accountChoosingRequired = false
      this.scheduleUpdate(500)
    },
    async cancelAuth () {
      this.loading = true
      try {
        const r = await BaseService.cancelAuth()
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(500)
    }
  },
  mounted () {
    this.updateStatus()
  }
}
</script>

<style lang="scss" scoped>
.loading-content {
  padding-top: 20vh;
}

.qrcode-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.qrcode-image {
  border: 1px solid #ccc;
}

.icon {
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  width: 1rem;
  height: 1rem;
}

.eye {
  background-image: url(../assets/eye.svg);
}

.eye-slash {
  background-image: url(../assets/eye-slash.svg);
}
</style>
